import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import {
  fetchItems,
  claimChest,
  setItemToCharacter,
  type InventoryItem,
  fetchCharacters,
  getCharactersFetchCacheKey,
} from './action'

// ✨ Inventory State Interface
export interface InventoryState {
  items: InventoryItem[]
  loot: InventoryItem[]
  /** Items / chest / equip flows — not character list fetch (avoids modals waiting on unrelated inventory HTTP). */
  isLoading: boolean
  /** Only `fetchCharacters` — use in character pickers so cached roster still renders while gems/items load. */
  isCharactersLoading: boolean
  error: string | null
  lastClaimed: Date | null
  characters: any[]
  weapons: any[]
  /** Last successful `fetchCharacters` identity — used to skip redundant identical refetches. */
  charactersCache: { walletAddress: string; scopeKey: string } | null
  /** Last successful `fetchItems` for this wallet (for skipping repeat page loads). */
  itemsCache: { walletAddress: string; fetchedAt: number } | null
}

const initialState: InventoryState = {
  items: [],
  loot: [],
  isLoading: false,
  isCharactersLoading: false,
  error: null,
  lastClaimed: null,
  characters: [],
  weapons: [],
  charactersCache: null,
  itemsCache: null,
}

export const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    // ✨ Clear inventory data
    clearInventory: (state) => {
      state.items = []
      state.loot = []
      state.error = null
      state.charactersCache = null
      state.itemsCache = null
    },

    // ✨ Update item stock
    updateItemStock: (state, action: PayloadAction<{ itemId: string; newStock: number }>) => {
      const { itemId, newStock } = action.payload
      const item = state.items.find(i => i._id === itemId)
      if (item) {
        item.stock = newStock
      }

      const lootItem = state.loot.find(i => i._id === itemId)
      if (lootItem) {
        lootItem.stock = newStock
      }
    },

    // ✨ Add new item
    addItem: (state, action: PayloadAction<InventoryItem>) => {
      const newItem = action.payload
      state.items.push(newItem)

      // Categorize item
      if (newItem.item === 'loot') {
        state.loot.push(newItem)
      }
    },

    // ✨ Remove item
    removeItem: (state, action: PayloadAction<string>) => {
      const itemId = action.payload
      state.items = state.items.filter(item => item._id !== itemId)
      state.loot = state.loot.filter(item => item._id !== itemId)
    },

    /** Immediate hospital heal: roster list can lag refetch — align HP with server full heal. */
    patchCharacterInstanceHp: (state, action: PayloadAction<{ characterId: string; currentHp: number }>) => {
      const id = String(action.payload.characterId ?? '')
      if (!id) return
      const hp = Number(action.payload.currentHp)
      if (!Number.isFinite(hp) || hp <= 0) return
      const char = state.characters.find((c: any) => String(c?._id ?? '') === id)
      if (!char) return
      if (!char.stats || typeof char.stats !== 'object') char.stats = {}
      char.stats.currentHp = hp
    },
  },
  extraReducers: (builder) => {
    // ✨ Fetch Items
    builder
      .addCase(fetchItems.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.isLoading = false
        state.items = action.payload.filter(item => item.item !== 'loot')
        state.loot = action.payload.filter(item => item.item === 'loot')
        state.itemsCache = {
          walletAddress: action.meta.arg.walletAddress,
          fetchedAt: Date.now(),
        }
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to fetch items'
      })
      .addCase(fetchCharacters.pending, (state) => {
        state.isCharactersLoading = true
        state.error = null
      })
      .addCase(fetchCharacters.fulfilled, (state, action) => {
        state.isCharactersLoading = false
        state.characters = action.payload
        state.charactersCache = {
          walletAddress: action.meta.arg.walletAddress,
          scopeKey: getCharactersFetchCacheKey(action.meta.arg),
        }
      })
      .addCase(fetchCharacters.rejected, (state, action) => {
        state.isCharactersLoading = false
        state.error = action.payload || 'Failed to fetch charaters'
      })

    // ✨ Claim Chest
    builder
      .addCase(claimChest.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(claimChest.fulfilled, (state, action) => {
        state.isLoading = false
        state.lastClaimed = new Date()

        // If new items were earned, add them to the inventory
        if (action.payload.earnedItems) {
          action.payload.earnedItems.forEach((item: InventoryItem) => {
            const existingItem = state.items.find(i => i._id === item._id)
            if (existingItem) {
              existingItem.stock += item.stock
            } else {
              state.items.push(item)

              // Categorize new item
              if (item.item === 'loot') {
                state.loot.push(item)
              }
            }
          })
        }
      })
      .addCase(claimChest.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to claim chest'
      })

    // ✨ Set Item to Character
    builder
      .addCase(setItemToCharacter.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(setItemToCharacter.fulfilled, (state) => {
        state.isLoading = false
        // Item successfully set to character
      })
      .addCase(setItemToCharacter.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to set item to character'
      })
  },
})

export const { clearInventory, updateItemStock, addItem, removeItem, patchCharacterInstanceHp } =
  inventorySlice.actions
export default inventorySlice.reducer
